import { BorderGridContainer, GridBox, P, PortableText } from '@ahmdigital/ui';
import { isEmpty } from 'lodash/fp';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';

import { ACQUISITION_PAGE_SLUGS, PAGE_TITLE_TEMPLATE } from '../../ahm-constants/ui';
import { VARIANT } from '../../components/offers/tile/constants';
import CustomerActions from '../../actions/customer';
import getContentToRender from './util/get-content-to-render';
import Head from '../../../utils/head';
import HeroVideoSection from '../../components/hero/video-section';
import mediaLandingPageData from '../media-landing/data';
import MeetTheTeam from '../offers/components/meet-the-team';
import OfferSection from '../media-landing/components/offer-section';
import offersPageData from '../offers/data';
import OfferTile from '../../components/offers/tile';
import ReferralActions from '../../actions/referral';
import ReferrerActions from '../../actions/referrer';
import selectHasPartnerReferrer from '../../selectors/select-has-partner-referrer';
import selectReferralCode from '../../selectors/select-referral-code';
import useQueryProductType from './hooks/use-query-product-type';
import type { AcquisitionLandingContent } from './types';
import type { AcquisitionLandingPageTemplateProps } from '../../../../components/page-templates/acquisition-landing-page-template';

const AcquisitionLandingPage = ({
  activeOffers,
  pageContent,
  placeholders,
  slug,
}: AcquisitionLandingPageTemplateProps) => {
  const dispatch = useDispatch();
  const productType = useQueryProductType();

  const { OFFERS_PAGE, MEDIA_LANDING_PAGE } = ACQUISITION_PAGE_SLUGS;
  const isOfferPage = slug === OFFERS_PAGE;
  // e.g. /health-insurance/join
  const isMediaLandingPage = slug === MEDIA_LANDING_PAGE;
  const applyProductTypeFromParam = isMediaLandingPage && !!productType;

  const hasPartnerReferrer = useSelector(selectHasPartnerReferrer);
  const hasReferralCode = Boolean(useSelector(selectReferralCode));

  useEffect(() => {
    if (isOfferPage) {
      dispatch(ReferralActions.reset());
      dispatch(ReferrerActions.setHasPartnerReferrer(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!applyProductTypeFromParam) {
      return;
    }
    dispatch(CustomerActions.changeProductType(productType));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productType]);

  const tableOfContents = (pageContent ?? [])
    .map((entry) => entry.item)
    .filter((item): item is AcquisitionLandingContent => item != null);
  const mediaLandingHeroBannerData = mediaLandingPageData?.sections?.['hero-banner'];

  const metaData = isOfferPage
    ? {
        description: offersPageData.meta.description,
        title: offersPageData.meta.title,
      }
    : {
        description: mediaLandingPageData.meta.description,
        title: mediaLandingPageData.meta.title,
      };

  return (
    <>
      <Head titleTemplate={PAGE_TITLE_TEMPLATE}>
        <title>{metaData.title}</title>
        <meta name="description" content={metaData.description} />
      </Head>
      {isOfferPage && !isEmpty(activeOffers) && (
        <BorderGridContainer hideBorderTop>
          <GridBox padding="0">
            <OfferTile
              placeholders={placeholders}
              overrideOffers={activeOffers}
              heading="Check out our offers"
              subheading="After a sweet deal on health insurance? Take a look at our latest offers and promotions for new joiners here."
              variant={VARIANT.HEADER}
              isBoxed={false}
            />
          </GridBox>
        </BorderGridContainer>
      )}
      {isMediaLandingPage && (
        <>
          {mediaLandingHeroBannerData?.video && (
            <BorderGridContainer hideBorderTop>
              <GridBox padding="0">
                <HeroVideoSection
                  footer={mediaLandingHeroBannerData.footer}
                  subtitle={
                    mediaLandingHeroBannerData?.subtitle ? (
                      <PortableText source={mediaLandingHeroBannerData?.subtitle} />
                    ) : (
                      <P>{mediaLandingPageData?.meta?.description}</P>
                    )
                  }
                  title={mediaLandingHeroBannerData?.title}
                  videoSrc={mediaLandingHeroBannerData.video}
                  videoTitle={mediaLandingHeroBannerData?.title}
                />
              </GridBox>
            </BorderGridContainer>
          )}
          {!hasPartnerReferrer && !hasReferralCode && (
            <OfferSection offers={activeOffers} placeholders={placeholders} />
          )}
        </>
      )}
      {getContentToRender(tableOfContents)}
      {isOfferPage && <MeetTheTeam />}
    </>
  );
};

export default AcquisitionLandingPage;
